import { makeStyles, shorthands, tokens } from '@fluentui/react-components'

export const useTimeCalendarStyles = makeStyles({
    card: {
        flexShrink: 0,
        ...shorthands.padding(0),
        ...shorthands.gap(0),
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingHorizontalM),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
    headerStacked: {
        flexDirection: 'column',
        alignItems: 'stretch',
        ...shorthands.gap(tokens.spacingVerticalXS),
    },
    headerGroup: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    headerGroupStacked: {
        flexDirection: 'column',
        alignItems: 'flex-end',
        ...shorthands.gap(0),
    },
    monthLabel: {
        fontWeight: tokens.fontWeightSemibold,
        minWidth: '110px',
        textAlign: 'center',
    },
    norm: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
    },
    body: {
        display: 'flex',
        alignItems: 'stretch',
    },
    topNav: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalXS),
        ...shorthands.padding(tokens.spacingVerticalXXS, 0),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
    sideNav: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingVerticalS),
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXS),
        ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
    },
    headerCell: {
        color: tokens.colorNeutralForeground2,
        fontWeight: tokens.fontWeightRegular,
        fontSize: tokens.fontSizeBase200,
        textAlign: 'center',
        ...shorthands.padding(tokens.spacingVerticalXS, 0),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
    cell: {
        textAlign: 'center',
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground2,
        ...shorthands.padding(tokens.spacingVerticalXS, 0),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    },
    cellCompact: {
        fontSize: tokens.fontSizeBase200,
    },
    weekCell: {
        width: '48px',
        color: tokens.colorNeutralForeground2,
    },
    weekCellCompact: {
        width: '24px',
        textAlign: 'left',
        ...shorthands.padding(tokens.spacingVerticalXS, 0, tokens.spacingVerticalXS, tokens.spacingHorizontalXS),
    },
    currentWeekCell: {
        color: tokens.colorBrandForeground1,
        fontWeight: tokens.fontWeightSemibold,
    },
    totalCell: {
        width: '72px',
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        ...shorthands.borderLeft('1px', 'solid', tokens.colorNeutralStroke2),
    },
    totalCellCompact: {
        width: 'auto',
        ...shorthands.borderLeft('0', 'none', 'transparent'),
    },
    dayButton: {
        display: 'inline-block',
        minWidth: 'unset',
        // Not the full cell width: the coloured cells of consecutive days would otherwise read as one bar.
        width: 'calc(100% - 8px)',
        ...shorthands.border('0', 'none', 'transparent'),
        ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalXS),
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
        backgroundColor: 'transparent',
        color: 'inherit',
        fontSize: 'inherit',
        fontFamily: tokens.fontFamilyBase,
        cursor: 'pointer',
        ':hover': {
            backgroundColor: tokens.colorBrandBackground2,
            color: tokens.colorBrandForeground1,
        },
    },
    today: {
        fontWeight: tokens.fontWeightBold,
        textDecorationLine: 'underline',
        color: tokens.colorNeutralForeground1,
    },
    red: {
        backgroundColor: tokens.colorPaletteRedBackground2,
        color: tokens.colorPaletteRedForeground2,
    },
    orange: {
        // Marigold rather than (dark) orange: in the dark theme orange is nearly indistinguishable from red.
        backgroundColor: tokens.colorPaletteMarigoldBackground2,
        color: tokens.colorPaletteMarigoldForeground2,
    },
    green: {
        backgroundColor: tokens.colorPaletteGreenBackground2,
        color: tokens.colorPaletteGreenForeground2,
    },
    // Fluent gives the switch a 41px tall row of its own: the indicator and the label each carry vertical
    // padding. Next to the 20px info label that reads as misaligned, so the padding goes and the group's
    // align-items lines everything up on the same centre line.
    colorsSwitch: {
        alignItems: 'center',
        height: 'auto',
        '& > *': {
            marginTop: 0,
            marginBottom: 0,
            paddingTop: 0,
            paddingBottom: 0,
        },
        // The icon sits on the text baseline of the label, which lifts it a few pixels above the indicator.
        '& label': {
            display: 'flex',
            alignItems: 'center',
        },
    },
    // Only meaningful while the colours are on: the icon then carries the three cell colours as a gradient,
    // painted through a gradient defined in the switch itself. Off it keeps the neutral currentColor.
    colorsIcon: {
        '& path': {
            fill: 'url(#timeCalendarColorsGradient)',
        },
    },
    // The gradient lives in an svg of its own; it only holds the definition and must not take up space.
    colorsGradientDefs: {
        position: 'absolute',
        width: 0,
        height: 0,
    },
    legend: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        ...shorthands.gap(tokens.spacingHorizontalM),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap(tokens.spacingHorizontalXS),
    },
    legendSwatch: {
        width: '10px',
        height: '10px',
        ...shorthands.borderRadius(tokens.borderRadiusSmall),
    },
    infoContent: {
        display: 'grid',
        gridTemplateColumns: 'auto 16px auto',
        columnGap: tokens.spacingHorizontalS,
        rowGap: tokens.spacingVerticalXXS,
        alignItems: 'center',
    },
    infoSpacer: {
        gridColumnStart: '1',
        gridColumnEnd: '-1',
        height: tokens.spacingVerticalS,
    },
    infoDivider: {
        gridColumnStart: '1',
        gridColumnEnd: '-1',
        height: '1px',
        backgroundColor: tokens.colorNeutralStroke2,
        ...shorthands.margin(tokens.spacingVerticalXXS, 0),
    },
    infoFullRow: {
        gridColumnStart: '1',
        gridColumnEnd: '-1',
    },
    tooltipContent: {
        display: 'flex',
        flexDirection: 'column',
    },
})
